import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { LabelValueSource, LabelValueAcceptedBy } from 'graphql/types';

import { useCustomLabelArray } from 'modules/customLabels/hooks';
import { useFeatureFlag } from 'modules/common/hooks';
import { CustomLabel } from '../labelSchema';

type useBulkCustomSuggestionsActionsProps = {
  fieldName: string;
};
type useBulkCustomSuggestionsActionsResult = {
  enableCustomActions: boolean;
  denyAllCustomLabelSuggestionHandler: () => void;
  acceptAllCustomLabelSuggestionHandler: () => void;
};
export const useBulkCustomSuggestionsActions = ({
  fieldName
}: useBulkCustomSuggestionsActionsProps): useBulkCustomSuggestionsActionsResult => {
  const { control } = useFormContext();
  const { replace } = useCustomLabelArray({ fieldName });
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );

  const fields = useWatch({
    name: `${fieldName}`,
    control
  });

  const enableCustomActions = useMemo(
    () =>
      (fields ?? []).some(
        (field: CustomLabel) =>
          field?.valueSource === LabelValueSource.Suggestion &&
          !field?.acceptedBy
      ),
    [fields]
  );

  const denyAllCustomLabelSuggestionHandler = useCallback(() => {
    const userFields = (fields ?? []).filter((field: CustomLabel) => {
      const value = isTimeSignalFederatedSuggestionsEnabled
        ? field?.suggestedValues?.[0]
        : field?.value;
      if (
        (field?.valueSource === LabelValueSource.User || field?.acceptedBy) &&
        value
      ) {
        return field;
      }
    });
    replace?.(userFields);
  }, [fields, isTimeSignalFederatedSuggestionsEnabled, replace]);

  const acceptAllCustomLabelSuggestionHandler = useCallback(() => {
    const userField = (fields ?? []).map((field: CustomLabel) => {
      const value = isTimeSignalFederatedSuggestionsEnabled
        ? field?.suggestedValues?.[0]
        : field?.value;
      if (
        field.valueSource === LabelValueSource.Suggestion &&
        value &&
        !field?.acceptedBy
      ) {
        return {
          ...field,
          acceptedBy: field.acceptedBy ?? LabelValueAcceptedBy.User,
          valueSource: field.valueSource ?? LabelValueSource.User,
          ...(isTimeSignalFederatedSuggestionsEnabled
            ? { value: field?.suggestedValues?.[0] }
            : {})
        };
      } else {
        return field;
      }
    });
    replace?.(userField);
  }, [fields, replace, isTimeSignalFederatedSuggestionsEnabled]);

  return {
    denyAllCustomLabelSuggestionHandler,
    enableCustomActions,
    acceptAllCustomLabelSuggestionHandler
  };
};
